import React, { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledCheckbox } from 'transfix-form/components'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Box, makeStyles } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import {
  ITransactionSetTypes,
  ITransactionSetSubtypes,
  ICreateOutboundFilesReport,
} from 'types/graphqlTypes'
import { brandColors, Button } from 'transfix-ui'
import { ShipmentPendingFiles } from '../../StagingTesting/ShipmentPendingFiles'
import { useStagingTestingCreateOutboundFiles } from '../../hooks/useStagingTestingCreateOutboundFiles'
import { useStagingTestingFiles } from '../../hooks/useStagingTestingFiles'
import { TCreateFilesSchema } from './CreateFilesSchema'
import { useCreateFilesFormContext } from './useCreateFilesForm'

const getErrorMessage = <T extends FieldErrors<TCreateFilesSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

const useStyles = makeStyles(() => ({
  error: {
    color: brandColors.error1,
  },
}))

export const CreateFilesForm = () => {
  const { control, errors, handleSubmit, watch } = useCreateFilesFormContext()

  const { createOutboundFiles } = useStagingTestingCreateOutboundFiles()

  const [outboundFilesReport, setOutboundFilesReport] = useState<ICreateOutboundFilesReport>()
  const shipmentIdWatched = watch('shipmentId')

  const clearReport = () => setOutboundFilesReport(undefined)

  const classes = useStyles()

  const {
    errorBacktrace = null,
    errorMessage = null,
    outboundRawEdiFileIds = [],
    outboundRawEdiFileUrls = [],
    success = false,
  } = outboundFilesReport || {}

  const createSubmitHandler = (
    transactionSet: ITransactionSetTypes,
    transactionSetSubtype: ITransactionSetSubtypes | null
  ) => {
    return handleSubmit(async ({ appointmentUpdates, shipmentId, stopUpdates }) => {
      clearReport()
      const testReport = await createOutboundFiles({
        shipmentId,
        transactionSet,
        transactionSetSubtype,
        appointmentUpdates,
        stopUpdates,
      })
      if (testReport) {
        setOutboundFilesReport(testReport)
      }
    })
  }

  const handle990 = createSubmitHandler(ITransactionSetTypes._990, null)
  const handle214Status = createSubmitHandler(ITransactionSetTypes._214, null)
  const handle214Scheduled = createSubmitHandler(
    ITransactionSetTypes._214,
    ITransactionSetSubtypes.SCHEDULED_TRACKING
  )
  const handle210 = createSubmitHandler(ITransactionSetTypes._210, null)

  const {
    deleteFiles,
    errorBacktrace: fileActionErrorBacktrace,
    errorMessage: fileActionErrorMessage,
    loadFiles,
    loading,
    pendingFiles,
    uploadFiles,
  } = useStagingTestingFiles()

  return (
    <form data-test='create-files-test'>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ControlledTextField
            name='shipmentId'
            control={control}
            TextInputProps={{
              label: 'Shipment ID',
              validationMessage: getErrorMessage(errors, 'shipmentId'),
              error: !!getErrorMessage(errors, 'shipmentId'),
              placeholder: 'Shipment ID',
              dataTest: 'input-shipmentId',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button color='primary' size='small' data-test='test-990' onClick={handle990}>
            Build 990
          </Button>
        </Grid>
        <Grid container item>
          <Grid item xs={4}>
            <Button color='primary' size='small' data-test='test-status' onClick={handle214Status}>
              Build All 214 Status Messages
            </Button>
          </Grid>
          <Grid item xs={4}>
            <ControlledCheckbox
              name='appointmentUpdates'
              control={control}
              CheckboxProps={{ label: 'Appointment Updates', dataTest: 'test-appt-box' }}
            />
          </Grid>
          <Grid item xs={4}>
            <ControlledCheckbox
              name='stopUpdates'
              control={control}
              CheckboxProps={{ label: 'Stop Updates', dataTest: 'test-stop-box' }}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Button color='primary' size='small' data-test='test-sched' onClick={handle214Scheduled}>
            Build 214 Scheduled Tracking (X6)
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button color='primary' size='small' data-test='test-210' onClick={handle210}>
            Build 210
          </Button>
        </Grid>
        <Grid container>
          {!success && errorMessage && (
            <Grid item data-test='test-error'>
              <Box className={classes.error}>
                Error: {errorMessage}
                {errorBacktrace}
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid container>
          {success && (
            <Grid container item data-test='test-success'>
              Success
              {outboundRawEdiFileIds.map((fileId: string, index: number) => {
                return (
                  <Grid key={fileId} container spacing={2}>
                    <Grid item>
                      <a
                        target='_blank'
                        rel='noopener noreferrer'
                        href={outboundRawEdiFileUrls[index]}>
                        {fileId}
                      </a>
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
      <hr />
      <ShipmentPendingFiles
        shipmentId={shipmentIdWatched}
        onDeleteSelectedFiles={deleteFiles}
        onLoadPendingFiles={loadFiles}
        onUploadSelectedFiles={uploadFiles}
        pendingFiles={pendingFiles}
        loading={loading}
        errorMessage={fileActionErrorMessage}
        errorBacktrace={fileActionErrorBacktrace}
      />
    </form>
  )
}
